import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheckCircle, faAward, faQuoteLeft, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { faLinkedin } from '@fortawesome/free-brands-svg-icons';
import css from './TrustStrip.css';
import config from '../../../config';
import {
  MIN_VERIFIED_TESTIMONIALS,
  MIN_VERIFIED_CERTIFICATIONS,
} from '../../../util/coachTier';

/**
 * TrustStrip — public panel shown on a coach's listing sidebar
 * (and other public surfaces) when they pass both verification gates.
 *
 * Reads the counts off `user.attributes.profile.publicData` (denormalised
 * by the backend on testimonial confirm / credential approval). If
 * neither gate is met it renders nothing — the strip is a positive
 * signal, never a "you're not verified" notice.
 *
 * On mount, fetches the actual testimonial + credential lists from the
 * public endpoints to render the rich UI.
 */
function TrustStrip({ user, coachId }) {
  const resolvedCoachId =
    coachId ||
    (user && user.id && (user.id.uuid || user.id)) ||
    null;

  const testimonialsCount =
    user?.attributes?.profile?.publicData?.verifiedTestimonialsCount || 0;
  const certificationsCount =
    user?.attributes?.profile?.publicData?.verifiedCertificationsCount || 0;
  const passes =
    testimonialsCount >= MIN_VERIFIED_TESTIMONIALS &&
    certificationsCount >= MIN_VERIFIED_CERTIFICATIONS;

  const [testimonials, setTestimonials] = useState([]);
  const [credentials, setCredentials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testimonialIndex, setTestimonialIndex] = useState(0);

  useEffect(() => {
    if (!passes || !resolvedCoachId) {
      setLoading(false);
      return;
    }
    let cancelled = false;
    Promise.all([
      axios
        .get(`${config.coachSaaSService}testimonials/public/${resolvedCoachId}?limit=10`)
        .catch(() => ({ data: { testimonials: [] } })),
      axios
        .get(`${config.coachSaaSService}credentials/public/${resolvedCoachId}?limit=10`)
        .catch(() => ({ data: { credentials: [] } })),
    ])
      .then(([tRes, cRes]) => {
        if (cancelled) return;
        setTestimonials((tRes.data && tRes.data.testimonials) || []);
        setCredentials((cRes.data && cRes.data.credentials) || []);
        setLoading(false);
      })
      .catch(() => {
        if (cancelled) return;
        setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [resolvedCoachId, passes]);

  // Hide entirely when not verified. A "not verified" notice would
  // hurt the coach unfairly; the absence of the strip is the signal.
  if (!passes) return null;

  // Brief skeleton while the lists load (data already gates the strip,
  // so we know there's at least 2 testimonials + 1 credential coming).
  if (loading) {
    return (
      <div className={css.strip}>
        <div className={css.headerStripe}>
          <FontAwesomeIcon icon={faCheckCircle} className={css.checkIcon} />
          <span className={css.headerText}>Verified Coach</span>
          <span className={css.headerSub}>Verified by Viveka</span>
        </div>
      </div>
    );
  }

  const current = testimonials[testimonialIndex] || null;
  const showCarouselControls = testimonials.length > 1;

  return (
    <div className={css.strip}>
      {/* ── Gold trust stripe ── */}
      <div className={css.headerStripe}>
        <FontAwesomeIcon icon={faCheckCircle} className={css.checkIcon} />
        <span className={css.headerText}>Verified Coach</span>
        <span className={css.headerSub}>Verified by Viveka</span>
      </div>

      {/* ── Credentials ── */}
      {credentials.length > 0 && (
        <div className={css.section}>
          <div className={css.sectionLabel}>Credentials</div>
          <div className={css.credentialGrid}>
            {credentials.slice(0, 4).map((c) => (
              <div key={c.id} className={css.credCard} title={`${c.issuingOrg} — ${c.title}`}>
                <div className={css.credCardIcon}>
                  <FontAwesomeIcon icon={faAward} />
                </div>
                <div className={css.credCardOrg}>{shortOrg(c.issuingOrg)}</div>
                <div className={css.credCardTitle}>{shortTitle(c.title)}</div>
                {c.issuedYear && <div className={css.credCardYear}>Since {c.issuedYear}</div>}
              </div>
            ))}
            {credentials.length > 4 && (
              <div className={`${css.credCard} ${css.credCardMore}`}>
                +{credentials.length - 4} more
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Testimonials carousel ── */}
      {testimonials.length > 0 && current && (
        <div className={css.section}>
          <div className={css.sectionLabel}>What clients say</div>
          <div className={css.testimonialCard}>
            <FontAwesomeIcon icon={faQuoteLeft} className={css.quoteIcon} />
            <p className={css.testimonialBody}>{current.body}</p>
            <div className={css.testimonialAttribution}>
              <span className={css.testimonialName}>{current.refereeName}</span>
              {(current.refereeTitle || current.refereeOrg) && (
                <span className={css.testimonialMeta}>
                  {[current.refereeTitle, current.refereeOrg].filter(Boolean).join(', ')}
                </span>
              )}
              {current.refereeLinkedInUrl && (
                <a
                  href={current.refereeLinkedInUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className={css.linkedinLink}
                  title="View referee on LinkedIn"
                >
                  <FontAwesomeIcon icon={faLinkedin} />
                </a>
              )}
            </div>
          </div>
          {showCarouselControls && (
            <div className={css.carouselControls}>
              <button
                type="button"
                className={css.carouselButton}
                onClick={() => setTestimonialIndex((i) => (i - 1 + testimonials.length) % testimonials.length)}
                aria-label="Previous testimonial"
              >
                <FontAwesomeIcon icon={faChevronLeft} />
              </button>
              <span className={css.carouselCount}>
                {testimonialIndex + 1} of {testimonials.length}
              </span>
              <button
                type="button"
                className={css.carouselButton}
                onClick={() => setTestimonialIndex((i) => (i + 1) % testimonials.length)}
                aria-label="Next testimonial"
              >
                <FontAwesomeIcon icon={faChevronRight} />
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Trim long organisation names for the small credential cards.
function shortOrg(s) {
  if (!s) return '';
  // Pull just the acronym in parens if present, e.g.
  // "International Coaching Federation (ICF)" → "ICF"
  const m = s.match(/\(([^)]{2,10})\)\s*$/);
  if (m) return m[1];
  if (s.length <= 24) return s;
  return s.slice(0, 22) + '…';
}

function shortTitle(s) {
  if (!s) return '';
  // Pull abbreviation in parens if present
  const m = s.match(/\(([A-Z]{2,8})\)/);
  if (m) return m[1];
  if (s.length <= 28) return s;
  return s.slice(0, 26) + '…';
}

export default TrustStrip;
